import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { Enrollment, Transaction } from '../types';
import './Pages.css';

const MyPoints: React.FC = () => {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusString = (status: unknown): 'Pending' | 'Approved' | 'Rejected' => {
    if (typeof status === 'string') {
      return status as 'Pending' | 'Approved' | 'Rejected';
    }
    if (typeof status === 'number') {
      // C# enum: Pending = 0, Approved = 1, Rejected = 2
      const statusMap: Record<number, 'Pending' | 'Approved' | 'Rejected'> = {
        0: 'Pending',
        1: 'Approved',
        2: 'Rejected'
      };
      return statusMap[status] || 'Pending';
    }
    return 'Pending'; // default fallback
  };

  const loadData = async () => {
    try {
      const [enrollmentsRes, transactionsRes] = await Promise.all([
        api.get<Enrollment[]>('/client/enrollments'),
        api.get<Transaction[]>('/client/transactions')
      ]);
      setEnrollments(enrollmentsRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Carregando...</div>;

  return (
    <div className="page">
      <div className="container">
        <h1>Meus Pontos</h1>
        <p className="subtitle">Acompanhe seus pontos e recompensas</p>

        <div className="enrollments-grid">
          {enrollments.map((enrollment) => (
            <div 
              key={enrollment.enrollmentId} 
              className="enrollment-card"
              onClick={() => setSelectedEnrollmentId(
                selectedEnrollmentId === enrollment.enrollmentId ? null : enrollment.enrollmentId
              )}
              style={{
                cursor: 'pointer',
                border: selectedEnrollmentId === enrollment.enrollmentId ? '2px solid #007bff' : '1px solid #e0e0e0',
                boxShadow: selectedEnrollmentId === enrollment.enrollmentId ? '0 4px 8px rgba(0, 123, 255, 0.2)' : '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease'
              }}
            >
              <h3>{enrollment.restaurantName || `Restaurante #${enrollment.restaurantId.substring(0, 8)}`}</h3>
              <div style={{ marginBottom: '12px', fontSize: '14px', color: '#666' }}>
                <div style={{ marginBottom: '4px' }}>
                  <strong>Plano:</strong> {enrollment.planName || 'N/A'}
                </div>
                <div>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    backgroundColor: enrollment.isPlanActive ? '#D1FAE5' : '#FEE2E2',
                    color: enrollment.isPlanActive ? '#065F46' : '#991B1B'
                  }}>
                    {enrollment.isPlanActive ? '✓ Ativo' : '✗ Inativo'}
                  </span>
                </div>
              </div>
              <div className="points-display">
                <div className="points-value">{enrollment.currentPoints}</div>
                <div className="points-label">pontos disponíveis</div>
              </div>
              <div className="enrollment-stats">
                <div className="stat">
                  <span className="stat-label">Total acumulado</span>
                  <span className="stat-value">{enrollment.totalPointsEarned} pts</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Recompensas resgatadas</span>
                  <span className="stat-value">{enrollment.rewardsRedeemed}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {enrollments.length === 0 && (
          <div className="empty-card">
            <h3>Você ainda não tem pontos</h3>
            <p>Cadastre sua primeira compra para começar a acumular!</p>
          </div>
        )}

        <h2 className="section-title">
          Histórico de Transações
          {selectedEnrollmentId && (
            <span style={{ 
              fontSize: '14px', 
              fontWeight: 'normal', 
              color: '#666',
              marginLeft: '8px'
            }}>
              - {enrollments.find(e => e.enrollmentId === selectedEnrollmentId)?.restaurantName || 'Filtrado'}
            </span>
          )}
        </h2>
        <div className="transactions-list">
          {(() => {
            const filteredTransactions = selectedEnrollmentId
              ? transactions.filter(t => {
                  const selectedEnrollment = enrollments.find(e => e.enrollmentId === selectedEnrollmentId);
                  return selectedEnrollment && t.restaurantId === selectedEnrollment.restaurantId;
                })
              : transactions;

            return filteredTransactions.map((transaction) => {
              const status = getStatusString(transaction.status);
              const isRedemption = transaction.source === 'Resgate';
              return (
                <div 
                  key={transaction.transactionId} 
                  className="transaction-item"
                  style={{
                    border: isRedemption ? '3px solid #F59E0B' : undefined,
                    backgroundColor: isRedemption ? '#FFFBEB' : undefined
                  }}
                >
                  <div className="transaction-left">
                    {isRedemption && (
                      <div style={{ 
                        marginBottom: '8px', 
                        padding: '6px 12px', 
                        backgroundColor: '#F59E0B', 
                        color: 'white', 
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '13px',
                        display: 'inline-block'
                      }}>
                        🎁 Resgate
                      </div>
                    )}
                    <div className="transaction-header">
                      <div className="transaction-date">
                        {new Date(transaction.transactionDate).toLocaleDateString('pt-BR')}
                      </div>
                      <div className="transaction-amount">
                        R$ {transaction.amount.toFixed(2)}
                      </div>
                    </div>
                    {status === 'Rejected' && transaction.rejectionReason && (
                      <div className="transaction-rejection-reason">
                        Motivo: {transaction.rejectionReason}
                      </div>
                    )}
                    {transaction.historyLog && transaction.historyLog.length > 0 && (
                      <div style={{ 
                        marginTop: '8px', 
                        paddingTop: '8px',
                        borderTop: '1px solid #e0e0e0',
                        fontSize: '12px',
                        color: '#666'
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>Histórico:</div>
                        <ul style={{ 
                          listStyle: 'none', 
                          padding: 0, 
                          margin: 0,
                          maxHeight: '100px',
                          overflowY: 'auto'
                        }}>
                          {transaction.historyLog.map((logEntry, index) => (
                            <li key={index} style={{ 
                              padding: '4px 0',
                              borderBottom: index < transaction.historyLog!.length - 1 ? '1px solid #f0f0f0' : 'none'
                            }}>
                              {logEntry}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="transaction-right">
                    <div className="transaction-points" style={{ color: isRedemption ? '#DC2626' : undefined, fontWeight: isRedemption ? 800 : undefined }}>
                      {transaction.pointsEarned} pts
                    </div>
                    <span className={`transaction-status ${status.toLowerCase()}`}>
                      {status === 'Pending' && '⏳ Pendente'}
                      {status === 'Approved' && '✓ Aprovado'}
                      {status === 'Rejected' && '✗ Rejeitado'}
                    </span>
                  </div>
                </div>
              );
            });
          })()}
        </div>

        {(() => {
          const filteredTransactions = selectedEnrollmentId
            ? transactions.filter(t => {
                const selectedEnrollment = enrollments.find(e => e.enrollmentId === selectedEnrollmentId);
                return selectedEnrollment && t.restaurantId === selectedEnrollment.restaurantId;
              })
            : transactions;

          if (filteredTransactions.length === 0) {
            return (
              <div className="empty-card">
                <p>
                  {selectedEnrollmentId 
                    ? 'Nenhuma transação para este restaurante' 
                    : 'Nenhuma transação ainda'}
                </p>
              </div>
            );
          }
          return null;
        })()}
      </div>
    </div>
  );
};

export default MyPoints;

